import { IconName } from 'components_new/atoms/Icon'

/**
 * The user facing categorisation of a connected data source. The backend has a
 * handful of technical types that collapse into fewer categories in the UI
 * (KPI_API and HIDDEN are both systems, for instance).
 */
export enum ESourceCategory {
  SYSTEM = 'system',
  DATABASE = 'database',
  FILE = 'file',
  CUSTOM = 'custom'
}

export const getSourceCategory = (apiType: string): ESourceCategory => {
  switch (apiType) {
  case 'OnPremiseDirect':
    return ESourceCategory.DATABASE
  case 'File':
    return ESourceCategory.FILE
  case 'OnPremise':
    return ESourceCategory.CUSTOM
  default:
    // API, KPI_API and HIDDEN are all systems, as is anything unknown.
    return ESourceCategory.SYSTEM
  }
}

/**
 * `path` is the url word the collect routes are built from. It is used both for
 * the link to a source (`/data-platform/collect/:path/:id/data`) and for the
 * create flows (`/data-platform/collect/:path/create`), and the specific pages
 * read it back out of the pathname, so it must not be renamed.
 */
export const SOURCE_CATEGORY_META: Record<
  ESourceCategory,
  { icon: IconName; label: string; path: string }
> = {
  [ESourceCategory.SYSTEM]: {
    icon: 'ApiOutlined',
    label: 'System',
    path: 'systems'
  },
  [ESourceCategory.DATABASE]: {
    icon: 'StorageOutlined',
    label: 'Databas',
    path: 'databases'
  },
  [ESourceCategory.FILE]: {
    icon: 'File',
    label: 'Fil',
    path: 'files'
  },
  [ESourceCategory.CUSTOM]: {
    icon: 'Settings',
    label: 'Anpassad',
    path: 'custom'
  }
}

/**
 * The categories a source can be created as. Custom sources are set up outside
 * the portal and therefore have no create flow.
 */
export const CREATABLE_SOURCE_CATEGORIES = [
  ESourceCategory.SYSTEM,
  ESourceCategory.DATABASE,
  ESourceCategory.FILE
]

export const AUTH_OPTIONS = [
  { title: 'Query', id: 'query' },
  { title: 'Headers', id: 'headers' }
]

/**
 * The flags on a source that decide what a connection to it is called. Declared
 * structurally rather than imported from types/GlobalSource, since that module
 * imports ESourceCategory from here.
 */
type TSourceFlags = {
  is_azure_data_tables: boolean
  is_custom_api: boolean
  is_dedu_sftp: boolean
  is_dynamics: boolean
  is_unit4_stored_procedure: boolean
  is_visma_control_edge: boolean
  is_visma_net: boolean
  is_vitec: boolean
  type: string
}

export type TCustomEndpointLabels = {
  /** The column next to the endpoint itself. */
  corresponding: string
  /** Tab label. */
  nav: string
  /** Used mid sentence: "Hantera <plural> för …". */
  plural: string
  singular: string
  /** Dynamics picks its endpoints from a list, everything else types them. */
  simpleDefinition: boolean
  /** Azure data tables have no corresponding table name. */
  skipCorresponding: boolean
}

/**
 * Sources that expose custom endpoints call them different things — views,
 * procedures, tenant ids. Returns null when the source has none at all, which
 * is also what hides the tab.
 */
export const getCustomEndpointLabels = (
  attributes: TSourceFlags
): TCustomEndpointLabels | null => {
  const isVisma = attributes.is_visma_net || attributes.is_visma_control_edge

  if (attributes.is_vitec) {
    return {
      corresponding: 'Tabell',
      nav: 'Vyer',
      plural: 'vyer',
      simpleDefinition: true,
      singular: 'Vy',
      skipCorresponding: false
    }
  }

  if (attributes.is_unit4_stored_procedure) {
    return {
      corresponding: 'Tabell',
      nav: 'Procedurer',
      plural: 'procedurer',
      simpleDefinition: true,
      singular: 'Procedur',
      skipCorresponding: false
    }
  }

  if (isVisma) {
    return {
      corresponding: 'Kund',
      nav: 'Tenant ids',
      plural: 'tenant ids',
      simpleDefinition: true,
      singular: 'Tenant id',
      skipCorresponding: false
    }
  }

  if (attributes.is_azure_data_tables) {
    return {
      corresponding: 'Tabell',
      nav: 'Tabeller',
      plural: 'tabeller',
      simpleDefinition: true,
      singular: 'Tabell',
      skipCorresponding: true
    }
  }

  if (attributes.is_dedu_sftp) {
    return {
      corresponding: 'Tabell',
      nav: 'Filer',
      plural: 'filer',
      simpleDefinition: true,
      singular: 'Filväg',
      skipCorresponding: false
    }
  }

  if (attributes.is_dynamics || attributes.is_custom_api) {
    return {
      corresponding: 'Tabell',
      nav: 'Endpoints',
      plural: 'endpoints',
      simpleDefinition: attributes.is_custom_api,
      singular: 'Endpoint',
      skipCorresponding: false
    }
  }

  return null
}

export enum ESourceTab {
  DATA = 'data',
  UPLOAD = 'upload',
  ENDPOINTS = 'endpoints'
}

export type TSourceTab = {
  icon: IconName
  label: string
  tab: ESourceTab
}

/**
 * The sections a source has. Which ones exist depends on how the data arrives:
 * files are uploaded, and only some systems expose custom endpoints.
 */
export const getSourceTabs = (attributes: TSourceFlags): TSourceTab[] => {
  const isFile = attributes.type === 'File'
  const customEndpoints = getCustomEndpointLabels(attributes)

  return [
    { icon: 'Table', label: 'Data', tab: ESourceTab.DATA },
    ...(isFile
      ? [
          {
            icon: 'CloudUploadOutlined' as IconName,
            label: 'Uppladdning',
            tab: ESourceTab.UPLOAD
          }
        ]
      : []),
    ...(customEndpoints
      ? [
          {
            icon: 'ApiOutlined' as IconName,
            label: customEndpoints.nav,
            tab: ESourceTab.ENDPOINTS
          }
        ]
      : [])
  ]
}
